import useClickOutside from '@/components/common/ClickOutSide';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'react-feather';

const ActionList = ({
    options, data, handleSelect, children, className, labelClass
}) => {
    const [show, setShow] = useState(false);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    const ref = useRef(); // menu ref
    const iconRef = useRef(); // trigger ref

    useClickOutside(ref, () => { setShow(false); });

    useEffect(() => {
        if (iconRef.current && ref.current && show) {
            const iconRect = iconRef.current.getBoundingClientRect();
            const tooltipWidth = ref.current.offsetWidth;
            const screenWidth = window.innerWidth;

            // center relative to trigger in viewport coords, then clamp
            let left = iconRect.left + (iconRect.width - tooltipWidth) / 2;
            left = Math.max(8, Math.min(left, screenWidth - tooltipWidth - 8));

            // position just below the trigger
            const top = iconRect.bottom + 6; // 6px gap

            setCoords({ top, left });
        }
    }, [show]);

    const handleClick = (e) => {
        e.stopPropagation();
        setShow(true);
        handleSelect(data);
    };

    const menu = show && (
        <div
            className="bg-gray-600 w-max z-[999] rounded-md overflow-hidden fixed mt-1.5"
            style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`
            }}
            ref={ref}
        >
            {options.map((item) => (
                <div
                    key={item.id}
                    onClick={() => {
                        item.onClick(data);
                        setShow(false);
                    }}
                    className={`${item.isHidden ? 'hidden' : ''} flex items-center gap-2 px-4 py-2 text-left text-gray-200 hover:text-gray-100
                        text-sm hover:bg-gray-500 cursor-pointer transition-all ${labelClass}`}
                >
                    {item?.icon && item.icon}
                    {item.label}
                </div>
            ))}
        </div>
    );

    return (
        <>
            <div className="relative text-center">
                {children ? (
                    <div
                        ref={iconRef}
                        onClick={handleClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleClick(e)}
                    >
                        {children}
                    </div>
                ) : (
                    <button
                        ref={iconRef}
                        className="more-options-btn text-center"
                        onClick={handleClick}
                        aria-haspopup="true"
                        aria-expanded={show}
                        type="button"
                    >
                        <MoreHorizontal size={22} className={className} />
                    </button>
                )}
            </div>

            {createPortal(menu, document.body)}
        </>
    );
};

ActionList.defaultProps = {
    handleSelect: () => { },
    children: '',
    options: [],
    className: '',
    labelClass: ''
};

ActionList.propTypes = {
    options: PropTypes.arrayOf(PropTypes.object),
    children: PropTypes.node,
    handleSelect: PropTypes.func,
    data: PropTypes.any,
    className: PropTypes.string,
    labelClass: PropTypes.string
};

export default ActionList;
